import type { CalloutBoard } from '@homecare/contracts';
import { Card, EmptyState, Measure, Spinner, StatusPill } from '@homecare/ui';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, Timer, UserCheck } from 'lucide-react';
import type { ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { instantText, secondsText } from '../../lib/schedule-time';

/**
 * Call-outs this week, and §14.5's number: median time to refill.
 *
 * The median, never the mean, and an open call-out is not counted as zero —
 * it has not been filled, so it has no time yet (RECOVERY §2). That is why
 * "no fills yet" reads as a dash and not as 0 s, which would say the opposite.
 */
export function CalloutsPanel({
  from,
  to,
  timeZone,
  onOpen,
}: {
  from: string;
  to: string;
  timeZone: string;
  onOpen: (visitId: string) => void;
}): ReactNode {
  const api = useApi();
  const board = useQuery({
    queryKey: ['schedule', 'callouts', from, to],
    queryFn: () => api.get<CalloutBoard>(`/v1/schedule/callouts?from=${from}&to=${to}`),
    staleTime: 0,
  });

  const data = board.data;
  const metrics = data?.metrics;

  return (
    <section aria-labelledby="callouts-heading" data-testid="callouts-panel">
      <Card className="grid gap-4">
        <h2 id="callouts-heading" className="text-ink font-semibold">
          Call-outs this week
        </h2>
        {board.isPending ? (
          <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
            <Spinner /> Loading call-outs…
          </p>
        ) : board.isError ? (
          <ErrorNote error={board.error} nextStep="Reload the page to try again." />
        ) : data && metrics ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Measure
                label="Median time to refill"
                icon={<Timer aria-hidden="true" className="h-5 w-5" />}
                tone={
                  metrics.medianSecondsToFill === null
                    ? 'inactive'
                    : metrics.medianSecondsToFill <= metrics.targetSeconds
                      ? 'verified'
                      : 'warning'
                }
                value={
                  metrics.medianSecondsToFill === null
                    ? undefined
                    : secondsText(metrics.medianSecondsToFill)
                }
                pending="A refill has to happen first"
                note={`Target ${secondsText(metrics.targetSeconds)}`}
              />
              <Measure
                label="Refilled"
                icon={<UserCheck aria-hidden="true" className="h-5 w-5" />}
                tone="verified"
                value={`${metrics.filled} of ${metrics.callouts}`}
                note={
                  metrics.withinTargetPct === null
                    ? 'None filled yet'
                    : `${metrics.withinTargetPct}% inside the target`
                }
              />
              <Measure
                label="Still open"
                icon={<CalendarClock aria-hidden="true" className="h-5 w-5" />}
                tone={metrics.open > 0 ? 'critical' : 'verified'}
                value={String(metrics.open)}
                note={metrics.open > 0 ? 'Open shifts are first on the board' : 'Nothing waiting'}
              />
            </div>
            {data.callouts.length === 0 ? (
              <EmptyState
                title="No call-outs this week"
                explanation="When a caregiver can’t make a visit, record it from the visit on the board with Call out. It lands here with how long the refill took."
              />
            ) : (
              <ul className="grid gap-2" aria-label="Call-outs">
                {data.callouts.map((callout) => (
                  <li key={callout.id}>
                    <button
                      type="button"
                      onClick={() => onOpen(callout.visitId)}
                      className="border-line hover:bg-surface-2 grid w-full gap-1 rounded-md border p-3 text-left text-sm"
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span className="text-ink font-semibold">
                          {callout.clientName} · {instantText(callout.visitStartsAt, timeZone)}
                        </span>
                        {callout.filledAt ? (
                          <StatusPill tone="verified">
                            Refilled in {secondsText(callout.secondsToFill ?? 0)}
                          </StatusPill>
                        ) : callout.needsEscalation ? (
                          <StatusPill tone="critical">Escalate</StatusPill>
                        ) : (
                          <StatusPill tone="warning">Open</StatusPill>
                        )}
                      </span>
                      <span className="text-ink-2">
                        {callout.calledOutCaregiverName} called out — “{callout.reason}”
                        {callout.filledCaregiverName
                          ? ` · ${callout.filledCaregiverName} took it${callout.fillSource === 'CLAIM' ? ' from an offer' : ''}`
                          : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}
      </Card>
    </section>
  );
}
